// AvailabilitySetter.jsx

import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Box,
  Stack,
  keyframes,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import useRealtimeAvailability from "../../hooks/useRealtimeAvailability";
import AvailabilityList from "./AvailabilityList";

// Bounce animation for arrows
const bounceAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(8px);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
`;

/**
 * Props:
 * - onAddAvailability, onPreviewChange
 * - propertiesSeed?: array from parent (Calendar) so dropdown is instant
 */
const AvailabilitySetter = forwardRef(
  ({ onAddAvailability, onPreviewChange, propertiesSeed = [] }, ref) => {
    const { saveAvailability, fetchUserProperties } = useRealtimeAvailability();

    // Seed immediately; if we have seed, don't show loading spinner.
    const [properties, setProperties] = useState(
      Array.isArray(propertiesSeed) ? propertiesSeed : []
    );
    const [propertiesLoading, setPropertiesLoading] = useState(
      (Array.isArray(propertiesSeed) ? propertiesSeed.length : 0) === 0
    );
    const [open, setOpen] = useState(false);

    // Form states
    const [selectedPropertyId, setSelectedPropertyId] = useState("");
    const [selectedDates, setSelectedDates] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [isMaintenance, setIsMaintenance] = useState(false);
    const [specificAvailability, setSpecificAvailability] = useState([]);
    const [saving, setSaving] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
    });

    useImperativeHandle(ref, () => ({
      open: (initialValues = {}) => {
        setSelectedDates(initialValues.selectedDates ?? []);
        setStartTime(initialValues.startTime ?? null);
        setEndTime(initialValues.endTime ?? null);

        if (initialValues.defaultPropertyId) {
          setSelectedPropertyId(initialValues.defaultPropertyId);
        } else if (!selectedPropertyId) {
          const first = (propertiesSeed && propertiesSeed[0]?.id) || (properties && properties[0]?.id);
          if (first) setSelectedPropertyId(first);
        }

        setOpen(true);
      },
    }));

    // Keep properties in sync if parent seed changes (e.g., first load)
    useEffect(() => {
      if (Array.isArray(propertiesSeed) && propertiesSeed.length > 0) {
        setProperties(propertiesSeed);
        setPropertiesLoading(false);
        if (!selectedPropertyId) {
          const first = propertiesSeed[0]?.id;
          if (first) setSelectedPropertyId(first);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertiesSeed]);

    // On open: if we still have no properties, fetch once and ALWAYS clear loading in finally.
    useEffect(() => {
      if (!open) return;

      if (Array.isArray(properties) && properties.length > 0) {
        setPropertiesLoading(false);
        return;
      }

      let cancelled = false;
      (async () => {
        setPropertiesLoading(true);
        try {
          const props = await fetchUserProperties();
          if (cancelled) return;
          if (Array.isArray(props)) {
            setProperties(props);
            if (!selectedPropertyId && props[0]?.id) {
              setSelectedPropertyId(props[0].id);
            }
          } else {
            setProperties([]);
          }
        } catch (err) {
          console.error("Error fetching properties:", err);
          if (!cancelled) setProperties([]);
        } finally {
          if (!cancelled) setPropertiesLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [open, fetchUserProperties, properties, selectedPropertyId]);

    // Preview to calendar
    useEffect(() => {
      if (!onPreviewChange) return;
      const preview = specificAvailability.map((a) => ({
        ...a,
        title: a.isMaintenance ? "Maintenance" : "Available",
        start: moment(a.startTime).toDate(),
        end: moment(a.endTime).toDate(),
        recurring: false,
        propertyId: selectedPropertyId || a.propertyId || "",
      }));
      onPreviewChange(preview);
    }, [specificAvailability, selectedPropertyId, onPreviewChange]);

    const resetForm = () => {
      setSelectedDates([]);
      setStartTime(null);
      setEndTime(null);
      setIsMaintenance(false);
      setSpecificAvailability([]);
      onPreviewChange?.([]);
    };

    const closeDialog = () => {
      setOpen(false);
      resetForm();
    };

    const addSpecificSlots = () => {
      if (!selectedPropertyId) return alert("Select a property first");
      if (!selectedDates.length || !startTime || !endTime)
        return alert("Select dates and times first");
      if (moment(endTime).isSameOrBefore(startTime))
        return alert("End time must be later than start time");

      const newAvail = selectedDates.flatMap((date) => {
        const base = moment(date).startOf("day");
        const slots = [];
        let cursor = moment(startTime);

        while (cursor.isBefore(endTime)) {
          const next = cursor.clone().add(15, "minutes");
          slots.push({
            id: uuidv4(),
            date: base.format("YYYY-MM-DD"),
            startTime: base.clone()
              .set({ hour: cursor.hour(), minute: cursor.minute(), second: 0, millisecond: 0 })
              .format("YYYY-MM-DDTHH:mm:ss"),
            endTime: base.clone()
              .set({ hour: next.hour(), minute: next.minute(), second: 0, millisecond: 0 })
              .format("YYYY-MM-DDTHH:mm:ss"),
            isMaintenance,
            propertyId: selectedPropertyId || "",
          });
          cursor = next;
        }
        return slots;
      });

      // Dedupe inside dialog
      setSpecificAvailability((prev) => {
        const seen = new Set(
          prev.map(
            (v) =>
              `${v.propertyId}|${v.date}|${v.startTime}|${v.endTime}|${v.isMaintenance}`
          )
        );
        const filtered = newAvail.filter(
          (v) =>
            !seen.has(
              `${v.propertyId}|${v.date}|${v.startTime}|${v.endTime}|${v.isMaintenance}`
            )
        );
        return filtered.length ? [...prev, ...filtered] : prev;
      });

      setSelectedDates([]);
      setStartTime(null);
      setEndTime(null);
      setIsMaintenance(false);
    };

    const clearAllPending = () => setSpecificAvailability([]);
    const clearPendingForSelectedDay = () => {
      if (!selectedDates.length) {
        alert("Pick a date on the inline calendar first to clear that day's pending slots.");
        return;
      }
      const target = moment(selectedDates[selectedDates.length - 1]).format("YYYY-MM-DD");
      setSpecificAvailability((prev) => prev.filter((a) => a.date !== target));
    };

    const handleSubmit = async () => {
      if (!selectedPropertyId)
        return alert("Select a property before saving.");
      const allAvailability = [...specificAvailability];
      if (!allAvailability.length)
        return alert("Add at least one slot before saving.");

      setSaving(true);
      try {
        const payload = allAvailability.map((a) => ({
          ...a,
          propertyId: selectedPropertyId,
        }));
        await saveAvailability(selectedPropertyId, payload);
        onAddAvailability?.(payload);
        closeDialog();
        setSnackbar({
          open: true,
          message: "Availability saved successfully!",
          severity: "success",
        });
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: "Could not save availability. Please try again.",
          severity: "error",
        });
      } finally {
        setSaving(false);
      }
    };

    // Helper: Check if user has selected dates and times but hasn't added slots yet
    const showAddSlotsHint = selectedDates.length > 0 && startTime && endTime && specificAvailability.length === 0;

    // Helper: Check if user has pending slots that need to be saved
    const showSaveHint = specificAvailability.length > 0;

    return (
      <>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Set Availability
        </Button>

        <Dialog
          open={open}
          onClose={saving ? undefined : closeDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Set Availability</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mb: 2 }} disabled={saving}>
              <InputLabel id="property-select-label">Select Property</InputLabel>
              <Select
                id="property-select"
                labelId="property-select-label"
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                label="Select Property"
                disabled={saving}
                renderValue={(val) => {
                  if (!val) return "Select Property";
                  const prop = properties.find((p) => p.id === val);
                  return prop
                    ? prop.name || prop.address || `Property ${prop.id}`
                    : "";
                }}
                MenuProps={{ disablePortal: false }}
              >
                {propertiesLoading && (
                  <MenuItem disabled>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading properties...
                    </Box>
                  </MenuItem>
                )}

                {!propertiesLoading && properties.length === 0 && (
                  <MenuItem disabled>No properties found</MenuItem>
                )}

                {!propertiesLoading &&
                  properties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || p.address || `Property ${p.id}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup fullWidth exclusive value="specific" sx={{ mb: 2 }}>
              <ToggleButton value="specific" disabled>
                Specific Dates
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography>Select dates:</Typography>
            <DatePicker
              inline
              minDate={new Date()}
              selected={null}
              highlightDates={selectedDates}
              onChange={(date) => setSelectedDates((prev) => [...prev, date])}
              disabled={saving}
            />

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  selected={startTime}
                  onChange={setStartTime}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Start"
                  dateFormat="h:mm aa"
                  placeholderText="Start time"
                  customInput={<TextField fullWidth label="Start Time" disabled={saving} />}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  selected={endTime}
                  onChange={setEndTime}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="End"
                  dateFormat="h:mm aa"
                  placeholderText="End time"
                  customInput={<TextField fullWidth label="End Time" disabled={saving} />}
                  disabled={saving}
                />
              </Grid>
            </Grid>

            {/* Step 1 Hint: Show arrow when dates & times are selected but no slots added yet */}
            {showAddSlotsHint && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  bgcolor: "#f4f4f9",
                  borderRadius: 2,
                  border: "2px dashed #81c241",
                  animation: `${pulseAnimation} 2s ease-in-out infinite`,
                }}
              >
                <ArrowDownwardIcon
                  sx={{
                    color: "#81c241",
                    fontSize: 28,
                    animation: `${bounceAnimation} 1s ease-in-out infinite`,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#333333", fontWeight: 600, textAlign: "center" }}
                >
                  Click the button below to add your selected timeslots
                </Typography>
                <ArrowDownwardIcon
                  sx={{
                    color: "#81c241",
                    fontSize: 28,
                    animation: `${bounceAnimation} 1s ease-in-out infinite`,
                  }}
                />
              </Box>
            )}

            <Button
              fullWidth
              sx={{
                mt: 2,
                // Highlight button when user should click it
                ...(showAddSlotsHint && {
                  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                  boxShadow: "0 0 12px rgba(129, 194, 65, 0.5)",
                  bgcolor: "#81c241",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#6aa832",
                  },
                }),
              }}
              variant={showAddSlotsHint ? "contained" : "outlined"}
              onClick={addSpecificSlots}
              disabled={saving}
            >
              Add Specific Availability
            </Button>

            {specificAvailability.length > 0 && (
              <>
                <AvailabilityList
                  specificAvailability={specificAvailability}
                  onRemoveSpecific={(index) =>
                    setSpecificAvailability((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={clearPendingForSelectedDay}>
                    Clear This Day (Pending)
                  </Button>
                  <Button size="small" color="error" variant="outlined" onClick={clearAllPending}>
                    Clear All Pending
                  </Button>
                </Stack>

                {/* Step 2 Hint: Show arrow pointing to Save button */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mt: 2,
                    p: 1.5,
                    bgcolor: "#f4f4f9",
                    borderRadius: 2,
                    border: "2px dashed #81c241",
                    animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  }}
                >
                  <CheckCircleOutlineIcon
                    sx={{
                      color: "#81c241",
                      fontSize: 24,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#333333", fontWeight: 600, textAlign: "center" }}
                  >
                    {specificAvailability.length} timeslot(s) ready! Click "Save" below to add them to the calendar
                  </Typography>
                  <ArrowForwardIcon
                    sx={{
                      color: "#81c241",
                      fontSize: 24,
                      animation: `${bounceAnimation} 1s ease-in-out infinite`,
                      transform: "rotate(90deg)",
                    }}
                  />
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              // Highlight save button area when there are pending slots
              ...(showSaveHint && {
                bgcolor: "#f4f4f9",
              }),
            }}
          >
            <Button onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || propertiesLoading}
              variant={showSaveHint ? "contained" : "text"}
              color={showSaveHint ? "success" : "primary"}
              sx={{
                ...(showSaveHint && {
                  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                  boxShadow: "0 0 12px rgba(129, 194, 65, 0.5)",
                  fontWeight: 700,
                  px: 4,
                }),
              }}
            >
              {saving ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3500}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }
);

export default AvailabilitySetter;
